import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Clock, Save, Send, AlertTriangle, CheckCircle, ArrowLeft, ArrowRight, Flag } from 'lucide-react';
import { enqueueSnackbar } from 'notistack';
import { api } from '../../services/api';
import { ProvaAcesso } from '../../types/provas';

interface ProvaQuestao {
  id: string;
  ordem: number;
  enunciado: string;
  tipo: 'multipla_escolha' | 'texto_livre';
  pontuacao: number;
  opcoes?: {
    id: string;
    letra: string;
    texto: string;
    ordem: number;
  }[];
}

interface ProvaResposta {
  id_questao: string;
  id_questao_opcao?: string;
  resposta_texto?: string;
  ordem_questao_prova: number;
}

interface RealizarProvaModalProps {
  isOpen: boolean;
  onClose: () => void;
  prova: ProvaAcesso;
  onProvaFinalizada: () => void;
}

export const RealizarProvaModal: React.FC<RealizarProvaModalProps> = ({
  isOpen,
  onClose,
  prova,
  onProvaFinalizada
}) => {
  const [questoes, setQuestoes] = useState<ProvaQuestao[]>([]);
  const [respostas, setRespostas] = useState<{ [questaoId: string]: ProvaResposta }>({});
  const [questaoAtual, setQuestaoAtual] = useState(0);
  const [tempoRestante, setTempoRestante] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [salvandoResposta, setSalvandoResposta] = useState(false);
  const [provaIniciada, setProvaIniciada] = useState(false);
  const [iniciadaEm, setIniciadaEm] = useState<Date | null>(null);
  const [tempoLimiteMinutos, setTempoLimiteMinutos] = useState<number | null>(null);
  const [showConfirmacao, setShowConfirmacao] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const debounceTimeouts = useRef<{ [key: string]: NodeJS.Timeout }>({});
  const provaInicializadaRef = useRef<boolean>(false);

  // Funções para persistência do estado da prova
  const salvarEstadoProva = useCallback(() => {
    if (!prova?.id) return;

    const estadoProva = {
      questoes,
      respostas,
      questaoAtual,
      tempoRestante,
      provaIniciada,
      iniciadaEm: iniciadaEm?.toISOString(),
      tempoLimiteMinutos,
      timestamp: Date.now()
    };

    localStorage.setItem(`prova_estado_${prova.id}`, JSON.stringify(estadoProva));
  }, [questoes, respostas, questaoAtual, tempoRestante, provaIniciada, iniciadaEm, tempoLimiteMinutos, prova?.id]);

  const restaurarEstadoProva = useCallback(() => {
    if (!prova?.id) return false;

    try {
      const estadoSalvo = localStorage.getItem(`prova_estado_${prova.id}`);
      if (!estadoSalvo) return false;

      const estado = JSON.parse(estadoSalvo);

      // Verificar se o estado não é muito antigo (máximo 24 horas)
      const agora = Date.now();
      const tempoLimite = 24 * 60 * 60 * 1000; // 24 horas

      if (agora - estado.timestamp > tempoLimite) {
        limparEstadoProva();
        return false;
      }

      // Restaurar estado
      if (estado.questoes && estado.questoes.length > 0) {
        setQuestoes(estado.questoes);
        setRespostas(estado.respostas || {});
        setQuestaoAtual(estado.questaoAtual || 0);

        // Restaurar dados do timer
        if (estado.iniciadaEm) {
          setIniciadaEm(new Date(estado.iniciadaEm));
        }
        if (estado.tempoLimiteMinutos) {
          setTempoLimiteMinutos(estado.tempoLimiteMinutos);
        }

        // Não restaurar tempoRestante, será recalculado pelo timer
        setTempoRestante(null);

        setProvaIniciada(estado.provaIniciada || false);
        return true;
      }

      return false;
    } catch (error) {
      console.error('Erro ao restaurar estado da prova:', error);
      limparEstadoProva();
      return false;
    }
  }, [prova?.id]);

  const limparEstadoProva = useCallback(() => {
    if (!prova?.id) return;
    localStorage.removeItem(`prova_estado_${prova.id}`);
  }, [prova?.id]);

  // Função removida temporariamente para evitar loops de requisições
  // const validarRespostasComServidor = useCallback(async () => { ... }, [prova?.id]);

  const handleClose = useCallback(() => {
    // Salvar estado antes de fechar se a prova estiver em andamento
    if (provaIniciada && questoes.length > 0) {
      salvarEstadoProva();
    }
    onClose();
  }, [provaIniciada, questoes.length, salvarEstadoProva, onClose]);

  useEffect(() => {
    if (isOpen && prova && !provaInicializadaRef.current) {
      provaInicializadaRef.current = true;

      // Tentar restaurar estado salvo primeiro
      const estadoRestaurado = restaurarEstadoProva();

      if (!estadoRestaurado) {
        // Se não conseguiu restaurar, seguir fluxo normal
        if (prova.status === 'disponivel') {
          iniciarProva();
        } else if (prova.status === 'em_andamento') {
          carregarProvaEmAndamento();
        }
      } else {
        // Estado restaurado, mostrar mensagem e iniciar timer se necessário
        enqueueSnackbar('Prova restaurada! Continuando de onde você parou.', { variant: 'info' });

        // Reinicializar timer baseado nos dados salvos
        if (iniciadaEm && tempoLimiteMinutos) {
          iniciarTimer(iniciadaEm, tempoLimiteMinutos);
        }
      }
    }

    return () => {
      if (!isOpen) {
        provaInicializadaRef.current = false;

        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }

        // Limpar todos os timeouts de debounce
        Object.values(debounceTimeouts.current).forEach(timeout => {
          if (timeout) clearTimeout(timeout);
        });
        debounceTimeouts.current = {};
      }
    };
  }, [isOpen, prova?.id]);

  // Salvar estado sempre que houver mudanças importantes (exceto tempoRestante)
  useEffect(() => {
    if (provaIniciada && questoes.length > 0) {
      salvarEstadoProva();
    }
  }, [questoes, respostas, questaoAtual, provaIniciada]);

  // Salvar estado quando usuário tentar sair da página
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (provaIniciada && questoes.length > 0) {
        salvarEstadoProva();
        // Avisar o usuário que há uma prova em andamento
        event.preventDefault();
        event.returnValue = 'Você tem uma prova em andamento. Tem certeza que deseja sair?';
        return 'Você tem uma prova em andamento. Tem certeza que deseja sair?';
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden' && provaIniciada && questoes.length > 0) {
        salvarEstadoProva();
      }
    };

    if (isOpen && provaIniciada) {
      window.addEventListener('beforeunload', handleBeforeUnload);
      document.addEventListener('visibilitychange', handleVisibilityChange);
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isOpen, provaIniciada, questoes.length, salvarEstadoProva]);

  const iniciarProva = async () => {
    try {
      setLoading(true);

      // Iniciar a prova
      await api.post(`/provas/acessos/${prova.id}/iniciar`);

      // Carregar questões
      const questoesResponse = await api.get(`/provas/acessos/${prova.id}/questoes`);
      setQuestoes(questoesResponse.data.questoes);

      // Carregar respostas já existentes (se houver)
      const respostasResponse = await api.get(`/provas/acessos/${prova.id}/respostas`);
      const respostasMap: { [questaoId: string]: ProvaResposta } = {};
      respostasResponse.data.forEach((resposta: ProvaResposta) => {
        respostasMap[resposta.id_questao] = resposta;
      });
      setRespostas(respostasMap);

      setProvaIniciada(true);

      // Buscar informações do acesso para inicializar timer
      const acessoResponse = await api.get(`/provas/acessos/${prova.id}`);
      const acessoData = acessoResponse.data;

      // Iniciar timer se houver tempo limite e data de início
      if (prova.prova?.tempo_limite_minutos && acessoData.iniciada_em) {
        const dataInicio = new Date(acessoData.iniciada_em);
        iniciarTimer(dataInicio, prova.prova.tempo_limite_minutos);
      }

    } catch (error) {
      console.error('Erro ao iniciar prova:', error);
      enqueueSnackbar('Erro ao iniciar prova', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const carregarProvaEmAndamento = async () => {
    try {
      setLoading(true);

      // Carregar questões
      const questoesResponse = await api.get(`/provas/acessos/${prova.id}/questoes`);
      setQuestoes(questoesResponse.data.questoes);

      // Carregar respostas existentes
      const respostasResponse = await api.get(`/provas/acessos/${prova.id}/respostas`);
      const respostasMap: { [questaoId: string]: ProvaResposta } = {};
      respostasResponse.data.forEach((resposta: ProvaResposta) => {
        respostasMap[resposta.id_questao] = resposta;
      });
      setRespostas(respostasMap);

      // Buscar informações do acesso para inicializar timer
      const acessoResponse = await api.get(`/provas/acessos/${prova.id}`);
      const acessoData = acessoResponse.data;

      setProvaIniciada(true);

      // Iniciar timer se houver tempo limite e data de início
      if (prova.prova?.tempo_limite_minutos && acessoData.iniciada_em) {
        const dataInicio = new Date(acessoData.iniciada_em);
        iniciarTimer(dataInicio, prova.prova.tempo_limite_minutos);
      }

    } catch (error) {
      console.error('Erro ao carregar prova em andamento:', error);
      enqueueSnackbar('Erro ao carregar prova', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const iniciarTimer = (dataInicio: Date, tempoLimiteMins: number) => {
    if (!tempoLimiteMins) {
      return;
    }

    // Evitar inicializar timer múltiplas vezes
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    setIniciadaEm(dataInicio);
    setTempoLimiteMinutos(tempoLimiteMins);

    // Calcular tempo restante baseado no horário atual vs horário de início
    const calcularTempoRestante = () => {
      const agora = new Date();
      const tempoDecorrido = Math.floor((agora.getTime() - dataInicio.getTime()) / 1000);
      const tempoLimiteSegundos = tempoLimiteMins * 60;
      const tempoRestanteCalculado = Math.max(0, tempoLimiteSegundos - tempoDecorrido);

      return tempoRestanteCalculado;
    };

    // Calcular tempo inicial
    const tempoInicial = calcularTempoRestante();
    setTempoRestante(tempoInicial);

    // Se já passou do tempo, finalizar
    if (tempoInicial <= 0) {
      finalizarProvaPorTempo();
      return;
    }

    // Atualizar timer a cada segundo
    intervalRef.current = setInterval(() => {
      const novoTempoRestante = calcularTempoRestante();
      setTempoRestante(novoTempoRestante);

      if (novoTempoRestante <= 0) {
        finalizarProvaPorTempo();
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      }
    }, 1000);
  };

  const salvarResposta = async (questaoId: string, resposta: Partial<ProvaResposta>) => {
    try {
      setSalvandoResposta(true);

      const payload = {
        id_questao: questaoId,
        id_questao_opcao: resposta.id_questao_opcao,
        resposta_texto: resposta.resposta_texto,
        ordem_questao_prova: resposta.ordem_questao_prova ?? 0
      };

      console.log('Salvando resposta:', payload);

      await api.post(`/provas/acessos/${prova.id}/respostas`, payload);

      // Atualizar estado local
      setRespostas(prev => ({
        ...prev,
        [questaoId]: {
          id_questao: questaoId,
          id_questao_opcao: resposta.id_questao_opcao,
          resposta_texto: resposta.resposta_texto,
          ordem_questao_prova: resposta.ordem_questao_prova || 0
        }
      }));

    } catch (error) {
      console.error('Erro ao salvar resposta:', error);
      enqueueSnackbar('Erro ao salvar resposta', { variant: 'error' });
    } finally {
      setSalvandoResposta(false);
    }
  };

  // Função para salvar resposta com debounce (para questões de texto)
  const salvarRespostaComDebounce = useCallback((questaoId: string, resposta: Partial<ProvaResposta>, delay: number = 1500) => {
    // Cancelar timeout anterior para esta questão
    if (debounceTimeouts.current[questaoId]) {
      clearTimeout(debounceTimeouts.current[questaoId]);
    }

    // Atualizar estado local imediatamente (para não perder a digitação)
    setRespostas(prev => ({
      ...prev,
      [questaoId]: {
        id_questao: questaoId,
        id_questao_opcao: resposta.id_questao_opcao,
        resposta_texto: resposta.resposta_texto,
        ordem_questao_prova: resposta.ordem_questao_prova || 0
      }
    }));

    // Criar novo timeout para salvar no servidor
    debounceTimeouts.current[questaoId] = setTimeout(() => {
      salvarResposta(questaoId, resposta);
      delete debounceTimeouts.current[questaoId];
    }, delay);
  }, [prova.id]);

  // Função para forçar salvamento imediato de todas as respostas pendentes
  const salvarRespostasPendentes = useCallback(async () => {
    const timeoutIds = Object.keys(debounceTimeouts.current);

    if (timeoutIds.length > 0) {
      // Cancelar todos os timeouts
      timeoutIds.forEach(questaoId => {
        if (debounceTimeouts.current[questaoId]) {
          clearTimeout(debounceTimeouts.current[questaoId]);
          delete debounceTimeouts.current[questaoId];
        }
      });
    }

    // Salvar todas as respostas que estão no estado local mas podem não ter sido enviadas
    const respostasParaSalvar = Object.entries(respostas);
    for (const [questaoId, resposta] of respostasParaSalvar) {
      if (resposta) {
        try {
          await api.post(`/provas/acessos/${prova.id}/respostas`, {
            id_questao: questaoId,
            id_questao_opcao: resposta.id_questao_opcao,
            resposta_texto: resposta.resposta_texto,
            ordem_questao_prova: resposta.ordem_questao_prova ?? 0
          });
        } catch (error) {
          console.error(`Erro ao salvar resposta da questão ${questaoId}:`, error);
        }
      }
    }
  }, [prova.id, respostas]);

  // Função para navegar entre questões salvando pendências
  const navegarParaQuestao = useCallback(async (novoIndice: number) => {
    // Salvar respostas pendentes antes de navegar
    await salvarRespostasPendentes();
    setQuestaoAtual(novoIndice);
  }, [salvarRespostasPendentes]);

  const verificarQuestoesRespondidas = (): { todasRespondidas: boolean; questoesPendentes: number[] } => {
    const questoesPendentes: number[] = [];

    questoes.forEach((questao, index) => {
      const resposta = respostas[questao.id];

      if (!resposta) {
        questoesPendentes.push(index + 1);
      } else {
        // Verificar se a resposta tem conteúdo válido
        if (questao.tipo === 'multipla_escolha' && !resposta.id_questao_opcao) {
          questoesPendentes.push(index + 1);
        } else if (
          questao.tipo === 'texto_livre' &&
          (!resposta.resposta_texto || resposta.resposta_texto.trim() === '')
        ) {
          questoesPendentes.push(index + 1);
        }
      }
    });

    return {
      todasRespondidas: questoesPendentes.length === 0,
      questoesPendentes
    };
  };

  const finalizarProva = async () => {
    try {
      setLoading(true);

      // Verificar se todas as questões foram respondidas
      const { todasRespondidas, questoesPendentes } = verificarQuestoesRespondidas();

      if (!todasRespondidas) {
        const questoesTexto = questoesPendentes.length === 1
          ? `questão ${questoesPendentes[0]}`
          : `questões ${questoesPendentes.join(', ')}`;

        enqueueSnackbar(
          `Você precisa responder todas as questões antes de finalizar a prova. Questões pendentes: ${questoesTexto}`,
          {
            variant: 'warning',
            autoHideDuration: 6000,
            anchorOrigin: { vertical: 'top', horizontal: 'center' }
          }
        );
        return;
      }

      // Salvar todas as respostas pendentes antes de finalizar
      await salvarRespostasPendentes();

      await api.post(`/provas/acessos/${prova.id}/finalizar`);

      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }

      // Limpar estado salvo após finalizar com sucesso
      limparEstadoProva();

      enqueueSnackbar('Prova finalizada com sucesso!', { variant: 'success' });
      onProvaFinalizada();
      onClose();

    } catch (error) {
      console.error('Erro ao finalizar prova:', error);
      enqueueSnackbar('Erro ao finalizar prova', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const finalizarProvaPorTempo = async () => {
    try {
      await api.post(`/provas/acessos/${prova.id}/finalizar-por-tempo`);

      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }

      // Limpar estado salvo após finalizar por tempo
      limparEstadoProva();

      enqueueSnackbar('Tempo esgotado! Prova finalizada automaticamente.', { variant: 'warning' });
      onProvaFinalizada();
      onClose();

    } catch (error) {
      console.error('Erro ao finalizar prova por tempo:', error);
    }
  };

  const handleRespostaMultiplaEscolha = (questaoId: string, opcaoId: string, ordem: number) => {
    const resposta: Partial<ProvaResposta> = {
      id_questao_opcao: opcaoId,
      ordem_questao_prova: ordem
    };

    salvarResposta(questaoId, resposta);
  };

  const handleRespostaDissertativa = (questaoId: string, texto: string, ordem: number) => {
    const resposta: Partial<ProvaResposta> = {
      resposta_texto: texto,
      ordem_questao_prova: ordem
    };

    // Usar debounce para questões de texto livre para evitar muitas requisições
    salvarRespostaComDebounce(questaoId, resposta);
  };

  const formatarTempo = (segundos: number): string => {
    // Validar se o valor é um número válido
    if (!segundos || isNaN(segundos) || segundos < 0) {
      return '--:--';
    }

    const horas = Math.floor(segundos / 3600);
    const minutos = Math.floor((segundos % 3600) / 60);
    const segs = segundos % 60;

    if (horas > 0) {
      return `${horas}:${minutos.toString().padStart(2, '0')}:${segs.toString().padStart(2, '0')}`;
    }
    return `${minutos}:${segs.toString().padStart(2, '0')}`;
  };

  const getCorTimer = (segundos: number): string => {
    if (segundos > 300) return 'text-green-600'; // > 5 min
    if (segundos > 60) return 'text-yellow-600';  // > 1 min
    return 'text-red-600'; // <= 1 min
  };

  const questaoSelecionada = questoes[questaoAtual];
  const respostaAtual = questaoSelecionada ? respostas[questaoSelecionada.id] : null;
  const totalRespondidas = Object.keys(respostas).length;

  if (!isOpen) return null;

  if (loading && !provaIniciada) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-card rounded-lg p-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <div>Preparando prova...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg w-full h-full max-w-6xl max-h-[95vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border bg-muted/30">
          <div>
            <h2 className="text-xl font-semibold">{prova.prova?.titulo}</h2>
            <p className="text-sm text-muted-foreground">
              Questão {questaoAtual + 1} de {questoes.length} |
              Respondidas: {totalRespondidas}/{questoes.length}
              {totalRespondidas < questoes.length && (
                <span className="ml-2 text-orange-600 font-medium">
                  ({questoes.length - totalRespondidas} pendentes)
                </span>
              )}
            </p>
          </div>

          <div className="flex items-center space-x-4">
            {tempoRestante !== null && (
              <div className={`flex items-center space-x-2 ${getCorTimer(tempoRestante)}`}>
                <Clock size={20} />
                <span className="font-mono text-lg font-semibold">
                  {formatarTempo(tempoRestante)}
                </span>
              </div>
            )}

            <button
              onClick={() => setShowConfirmacao(true)}
              className="text-muted-foreground hover:text-foreground"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Navegação das questões */}
        <div className="p-4 border-b border-border bg-muted/30">
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm font-medium text-foreground">Navegação:</span>
            <div className="flex space-x-2">
              <button
                onClick={() => setShowConfirmacao(true)}
                className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 flex items-center space-x-2"
              >
                <Send size={16} />
                <span>Finalizar Prova</span>
              </button>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            {questoes.map((_, index) => (
              <button
                key={index}
                onClick={() => navegarParaQuestao(index)}
                className={`w-8 h-8 rounded text-sm font-medium ${
                  index === questaoAtual
                    ? 'bg-blue-500 text-white'
                    : respostas[questoes[index]?.id]
                    ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 border border-green-300 dark:border-green-700'
                    : 'bg-muted text-muted-foreground border border-border'
                } hover:opacity-80`}
              >
                {index + 1}
              </button>
            ))}
          </div>
        </div>

        {/* Conteúdo da questão */}
        <div className="flex-1 p-6 overflow-y-auto">
          {questaoSelecionada && (
            <div className="space-y-6">
              <div>
                <div className="flex items-start justify-between mb-4">
                  <h3 className="text-lg font-semibold">
                    Questão {questaoAtual + 1}
                  </h3>
                  <span className="text-sm text-muted-foreground">
                    {questaoSelecionada.pontuacao} ponto(s)
                  </span>
                </div>

                <div className="prose dark:prose-invert max-w-none mb-6 whitespace-pre-wrap">
                  {questaoSelecionada.enunciado}
                </div>
              </div>

              {/* Múltipla escolha */}
              {questaoSelecionada.tipo === 'multipla_escolha' && questaoSelecionada.opcoes && (
                <div className="space-y-3">
                  {questaoSelecionada.opcoes.map((opcao) => (
                    <label
                      key={opcao.id}
                      className="flex items-start space-x-3 p-3 border border-border rounded-lg cursor-pointer hover:bg-muted"
                    >
                      <input
                        type="radio"
                        name={`questao_${questaoSelecionada.id}`}
                        value={opcao.id}
                        checked={respostaAtual?.id_questao_opcao === opcao.id}
                        onChange={() => handleRespostaMultiplaEscolha(
                          questaoSelecionada.id,
                          opcao.id,
                          questaoSelecionada.ordem
                        )}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <span className="font-medium mr-3">{opcao.letra})</span>
                        <span>{opcao.texto}</span>
                      </div>
                    </label>
                  ))}
                </div>
              )}

              {/* Texto Livre */}
              {questaoSelecionada.tipo === 'texto_livre' && (
                <div>
                  <textarea
                    value={respostaAtual?.resposta_texto || ''}
                    onChange={(e) => handleRespostaDissertativa(
                      questaoSelecionada.id,
                      e.target.value,
                      questaoSelecionada.ordem
                    )}
                    placeholder="Digite sua resposta aqui..."
                    className="w-full h-40 p-3 border border-border rounded-md bg-background text-foreground resize-none focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>
              )}



              {salvandoResposta && (
                <div className="flex items-center space-x-2 text-blue-600">
                  <Save size={16} className="animate-spin" />
                  <span className="text-sm">Salvando resposta...</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer com navegação */}
        <div className="flex items-center justify-between p-6 border-t border-border bg-muted/30">
          <button
            onClick={() => navegarParaQuestao(Math.max(0, questaoAtual - 1))}
            disabled={questaoAtual === 0}
            className="flex items-center space-x-2 px-4 py-2 bg-muted text-foreground rounded-md hover:bg-muted/80 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ArrowLeft size={16} />
            <span>Anterior</span>
          </button>

          <div className="text-sm text-muted-foreground">
            {respostaAtual ? (
              <span className="flex items-center text-green-600 dark:text-green-400">
                <CheckCircle size={16} className="mr-1" />
                Respondida
              </span>
            ) : (
              <span className="flex items-center text-muted-foreground">
                <Flag size={16} className="mr-1" />
                Não respondida
              </span>
            )}
          </div>

          <button
            onClick={() => navegarParaQuestao(Math.min(questoes.length - 1, questaoAtual + 1))}
            disabled={questaoAtual === questoes.length - 1}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span>Próxima</span>
            <ArrowRight size={16} />
          </button>
        </div>
      </div>

      {/* Modal de confirmação */}
      {showConfirmacao && (
        <div className="fixed inset-0 bg-black/75 flex items-center justify-center z-50">
          <div className="bg-card rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center space-x-3 mb-4">
              <AlertTriangle size={24} className="text-yellow-500" />
              <h3 className="text-lg font-semibold">Confirmar ação</h3>
            </div>

            <p className="text-muted-foreground mb-4">
              O que você gostaria de fazer?
            </p>

            <div className="text-sm text-muted-foreground mb-6">
              <p>Questões respondidas: {totalRespondidas} de {questoes.length}</p>
              <p>Questões não respondidas: {questoes.length - totalRespondidas}</p>
            </div>

            <div className="flex flex-col space-y-3">
              <button
                onClick={() => setShowConfirmacao(false)}
                className="px-4 py-2 border border-border rounded-md hover:bg-muted"
              >
                Continuar Prova
              </button>
              <button
                onClick={handleClose}
                className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
              >
                Sair e Salvar Progresso
              </button>
              <button
                onClick={finalizarProva}
                disabled={loading}
                className={`px-4 py-2 text-white rounded-md disabled:opacity-50 ${
                  verificarQuestoesRespondidas().todasRespondidas
                    ? 'bg-green-500 hover:bg-green-600'
                    : 'bg-orange-500 hover:bg-orange-600'
                }`}
              >
                {loading ? 'Finalizando...' : (
                  verificarQuestoesRespondidas().todasRespondidas
                    ? 'Finalizar Prova'
                    : `Finalizar Prova (${verificarQuestoesRespondidas().questoesPendentes.length} pendentes)`
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
